export const CONFIDENCE_LABELS = [
  'established',
  'corroborated',
  'reported',
  'single_source',
  'unverified',
] as const;
export type ConfidenceLabel = (typeof CONFIDENCE_LABELS)[number];

export const SOURCE_TIERS = ['authoritative', 'research', 'specialist', 'general'] as const;
export type SourceTier = (typeof SOURCE_TIERS)[number];

export const STORY_STATUS = ['active', 'archived'] as const;
export type StoryStatus = (typeof STORY_STATUS)[number];

export const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
export type Severity = (typeof SEVERITIES)[number];

export interface PublisherBadge {
  name: string;
  tier: SourceTier;
}

export interface StoryCardShape {
  id: string;
  title: string;
  publisherBadge: PublisherBadge;
  confidenceLabel: ConfidenceLabel;
  sourceCount: number;
  independentOrgs: number;
  section: string;
  maxRiskScore: number;
  maxSeverity: Severity | null;
  earliestPublishedAt: string;
  latestSourceAddedAt: string;
  excerpt: string;
  isUnverified: boolean;
  /** Sovereign-CERT v1: ISO-3166 alpha-2 of the publisher origin, when known.
   *  Null/undefined for stories whose canonical article has no source.country. */
  originCountry?: string | null;
  /**
   * KAN-822 — the STORY's distribution class ('mainstream' | 'vendor'), used
   * only to label Vendor stories. Optional: absent on older responses.
   */
  distributionClass?: string;
  /** Canonical `vendor-update-subtype` slug; null unless the class is vendor. */
  vendorUpdateSubtype?: string | null;
}

/**
 * Ontology tags surfaced on detail responses for the per-entity
 * `<ChannelCTA>` resolver. Mirrors the API's `DetailOntologyTags` shape and
 * the `<ChannelCTA>` `ontologyTags` prop. `security-domain` is currently
 * always empty — stories carry no native security-domain column — but is
 * present for shape-symmetry with the frontend priority walk.
 */
export interface DetailOntologyTags {
  'security-domain': string[];
  industry: string[];
  region: string[];
}

export interface RelatedSupplyChainEntry {
  providerSlug: string;
  providerName: string;
  recentIncidentCount: number;
  lastCriticalAt: string | null;
}

export interface StoryDetailShape extends StoryCardShape {
  synthesisedSummary: string;
  primaryCves: string[];
  primaryThreatActors: string[];
  /**
   * Phase 2 vendor unification (Plan T7 + T11) — vendor namespace shape
   * `{id, name, tier, aliasOf?}` resolved server-side from
   * `stories.primary_vendors uuid[]`. Replaces the legacy `string[]` of
   * display names. Render with the Tier-1 pill when `tier === 1`.
   */
  primaryVendors: import('../lib/types').VendorRef[];
  /**
   * Phase 2 vendor unification (Plan T7 + T8 + T11) — person namespace shape
   * resolved from `stories.primary_persons uuid[]`. Empty for stories whose
   * canonical article carries no named-individual entities.
   */
  primaryPersons: import('../lib/types').PersonRef[];
  primaryVictims: string[];
  canonicalArticleId: string;
  status: StoryStatus;
  createdAt: string;
  updatedAt: string;
  ontologyTags: DetailOntologyTags;
  /** Plan B Plan 2 T9: server-side enrichment with related leak-site activity
   *  when `story.threatActors[]` overlaps with active `ransomware_groups`.
   *  Optional — older API responses (pre-T9) omit this field, in which case
   *  the `<LeakSiteActivityCallout>` consumer simply renders nothing. */
  relatedLeakSiteActivity?: Array<{
    groupName: string;
    recentClaimCount: number;
    lastActiveAt: string;
  }>;
  /**
   * Plan C Plan 2 T10 — supply-chain provider rollups linked from this
   * story's vendor + ontology context. Empty array (never missing) when no
   * vendors map to a tracked supply-chain provider.
   */
  relatedSupplyChain?: RelatedSupplyChainEntry[];
  /**
   * Charter 23 T7 (KAN-150) — editorial conflict-of-interest disclosure,
   * derived server-side from the CyberFind vendor commercial-relationship
   * register. `null` (or absent, on pre-T7 API responses) means nothing to
   * disclose. When present it MUST be rendered above the story body — see
   * `@/components/stories/EditorialCoiDisclosure`.
   */
  coiDisclosure?: string | null;
  /** Vendor display names covered by `coiDisclosure`. */
  coiDisclosedVendors?: string[];
}

/**
 * KAN-48 — full-text source policy stable unions, re-exported as web-local
 * unions for compactness. Mirrors `@intelhub/shared` `FullTextStatus`,
 * `FullTextReason`, `FullTextFetchMode`, `FullTextPolicy`. Diagnostic
 * `full_text_fetch_meta` is intentionally absent — it is operator-only and
 * must not appear on web shapes.
 */
export const FULL_TEXT_STATUSES = [
  'available',
  'unavailable',
  'skipped',
  'failed',
] as const;
export type FullTextStatus = (typeof FULL_TEXT_STATUSES)[number];

export const FULL_TEXT_REASONS = [
  'none',
  'disabled',
  'denylisted',
  'link_only_source',
  'standard_fetch_only',
  'blocked_or_challenge',
  'paywall_or_registration',
  'non_2xx',
  'timeout',
  'content_too_short',
  'readability_empty',
  'extractor_error',
  'unknown',
] as const;
export type FullTextReason = (typeof FULL_TEXT_REASONS)[number];

export const FULL_TEXT_FETCH_MODES = [
  'none',
  'standard_http',
  'structured_extractor',
] as const;
export type FullTextFetchMode = (typeof FULL_TEXT_FETCH_MODES)[number];

export const FULL_TEXT_POLICIES = [
  'link_only',
  'standard_fetch_only',
  'structured_extractor_allowed',
] as const;
export type FullTextPolicy = (typeof FULL_TEXT_POLICIES)[number];

export interface StorySourceShape {
  articleId: string;
  title: string;
  publisher: string;
  sourceName: string;
  sourceTier: SourceTier;
  url: string;
  publishedAt: string;
  isCanonical: boolean;
  fullText: string | null;
  fullTextFetchedAt: string | null;
  fullTextMeta?: import('@intelhub/shared').ExploitDbExtraction | null;
  // KAN-48 — stable nullable availability fields. `full_text_fetch_meta` is
  // operator-only and must NEVER be added here.
  fullTextStatus?: FullTextStatus | null;
  fullTextReason?: FullTextReason | null;
  fullTextFetchMode?: FullTextFetchMode | null;
  fullTextPolicy?: FullTextPolicy | null;
}

export interface StoryListResponse {
  stories: StoryCardShape[];
  nextCursor: string | null;
}

export interface StorySearchResponse {
  stories: StoryCardShape[];
}

export interface StorySourcesResponse {
  sources: StorySourceShape[];
}

export interface StoryFilters {
  section?: string;
  confidence?: ConfidenceLabel;
  severity?: Severity;
  /**
   * P4a (audit item 5): source-tier filter. Backed by `sources.tier`
   * via the canonical article join. Mirrors the route's `tier` Zod enum.
   */
  tier?: SourceTier;
  /**
   * P4a (audit item 5): industry filter. Backed by `articles.industries`
   * arrayOverlaps; one industry slug at a time (the rail emits the
   * selected slug or undefined for "all").
   */
  industry?: string;
  /** Stories `latestSourceAddedAt` lower bound (ISO-8601). */
  from?: string;
  /** Stories `latestSourceAddedAt` upper bound (ISO-8601). */
  to?: string;
  cursor?: string;
  limit?: number;
  /** Sovereign-CERT v1 / P4a: one or more publisher-origin ISO-3166 α2 codes. */
  country?: string[];
  /**
   * KAN-822 T9 — the dedicated Vendor view (spec R14): a browsing choice for
   * this request only; it reads and writes no saved preference.
   */
  view?: 'vendor';
}

/**
 * P4a (audit item 5) — preset time-window selector for the Stories rail.
 * Maps to ISO-8601 `from` (lower bound on `latestSourceAddedAt`); `all` clears
 * the bound. Kept narrow on purpose: open-ended date pickers are out of scope
 * for Wave-1.
 */
export const STORY_TIME_WINDOWS = ['24h', '7d', '30d', '90d', 'all'] as const;
export type StoryTimeWindow = (typeof STORY_TIME_WINDOWS)[number];

export type AuditAction =
  | 'pipeline_create'
  | 'pipeline_add_member'
  | 'pipeline_synthesise'
  | 'admin_eject'
  | 'admin_merge'
  | 'admin_split'
  | 'admin_canonical_override'
  | 'admin_confidence_override'
  | 'admin_reset'
  | 'admin_resolve_tiers';

export interface AuditLogEntry {
  id: string;
  storyId: string;
  actorType: 'system' | 'admin';
  actorId: string | null;
  action: AuditAction;
  reason: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface AuditLogFilters {
  storyId?: string;
  actorId?: string;
  action?: AuditAction;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export type TimeRange = '7d' | '30d' | '90d';

export interface StoryAnalyticsTotals {
  total: number;
  pctEstablished: number;
  pctSingleSource: number;
  avgClusterSize: number;
  priorPeriod: {
    total: number;
    pctEstablished: number;
    pctSingleSource: number;
    avgClusterSize: number;
  };
}

export interface StoryAnalytics {
  totals: StoryAnalyticsTotals;
  confidenceLadder: Array<{ label: ConfidenceLabel; count: number }>;
  sourceTierMix: Array<{
    section: string;
    authoritative: number;
    research: number;
    specialist: number;
    general: number;
  }>;
  singleSourceRisk: {
    current: number;
    prior: number;
    daily: Array<{ date: string; pct: number }>;
  };
  storiesPerDay: Array<{ date: string; count: number }>;
  geographyTopN: Array<{ jurisdiction: string; count: number }>;
  industryConfidence: Array<{
    industry: string;
    counts: Partial<Record<ConfidenceLabel, number>>;
  }>;
  /** Section distribution trend — date-bucketed counts keyed by section slug.
   *  Daily buckets for 7d, weekly for 30d/90d. Used by AttentionMix widget. */
  sectionTrend: Array<{ date: string; counts: Record<string, number> }>;
}
